<template>
  <cs-preloader-overlay :loading="loading">
    <div :class="$style.container">

      <div :class="$style.header">
        <responsive-controller>
          <template #tablet>
            <div>
              <screen-header
                :title="$t($route.meta.title)"
                :history-mode="!isMobileOnly"
                @navigate="navigateOnMobile"
              >
                <help-link
                  v-if="$route.meta.help"
                  slot="toolbar"
                  :source="$route.meta.help"
                />
              </screen-header>

              <div
                v-if="$route.meta.navigation"
                :class="$style.navigation"
              >
                <navigation-controller />
              </div>
            </div>
          </template>

          <template #default>
            <div :class="$style.navigation">
              <navigate-button
                v-absolute
                v-left="-80"
                @navigate="navigateOnDesktop"
              />
              <navigation-controller />
            </div>
          </template>
        </responsive-controller>
      </div>

      <router-view
        v-if="getAllWidgets"
        :widgets="getAllWidgets"
        @remove="onRemove"
        @change-status="onChangeStatus"
        @submit="onManagingWidgetCreation"
      />

      <widget-save-confirmation
        v-model="isShownSaveConfirmation"
        :sender-fields="requiredSenderFields"
        @confirmation="onConfirmationReceived"
      />

    </div>
  </cs-preloader-overlay>
</template>

<script>
import { isMobileOnly } from 'mobile-device-detect';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import throttle from 'lib/utils/throttle';

import {
  create as createWidget,
  update as updateWidget,
  remove as removeWidget
} from 'services/widget';

import { update as updateProject } from 'services/project';

import HelpLink from 'molecules/HelpLink';
import NavigateButton from 'molecules/common/NavigateButton';
import ScreenHeader from 'molecules/screen/ScreenHeader';

import NavigationController from 'molecules/streamer/widgets/NavigationController';
import WidgetSaveConfirmation from 'organisms/streamer/widgets/WidgetSaveConfirmation';

import { SENDER_FIELDS_BY_WIDGET } from 'definitions/streamer/widgets';

// TODO: если данные расчеты потребуются где-нибудь еще, вынести в lib/utils/DOM.js
function isOffsetExceedsOrEqualsHeight() {
  const scrollTop = document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset || 0;
  const offset = scrollTop + window.innerHeight + 60;
  const height = document.documentElement.offsetHeight;

  return offset >= height;
}

const BASE_WIDGETS_ROUTE_LIST = [
  '/streamer/widgets/goal',
  '/streamer/widgets/notification',
  '/streamer/widgets/message',
  '/streamer/widgets/donors',
];

const GOAL_FORM_WIDGET_ROUTE = '/streamer/widgets/goal/form';

export default {
  name: 'StreamerWidgets',
  components: {
    HelpLink,
    NavigateButton,
    ScreenHeader,
    NavigationController,
    WidgetSaveConfirmation,
  },
  created() {
    this.loadData();
  },
  mounted() {
    document.addEventListener('scroll', throttle(this.handleScroll, 500));
  },
  beforeDestroy() {
    document.removeEventListener('scroll', throttle(this.handleScroll, 500));
  },
  data() {
    return {
      loading: false,
      currentWidget: null,
      isShownSaveConfirmation: false,
      isMobileOnly,
    };
  },
  computed: {
    requiredSenderFields() {
      if (this.$validate.isNull(this.currentWidget)) {
        return [];
      }

      if (!this.$validate.isObjectWithFields(SENDER_FIELDS_BY_WIDGET, [this.currentWidget.type])) {
        return [];
      }

      return SENDER_FIELDS_BY_WIDGET[this.currentWidget.type];
    },
    ...mapGetters(['getCurrentProject']),
    ...mapGetters('streamerWidgets', [
      'getAllWidgets',
      'getCurrentPage',
      'isLastDataChunk',
      'getDonationGoal',
    ]),
  },
  watch: {
    $route: {
      handler: 'loadData',
    },
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.RESET_DATA();

      try {
        await this.fetchWidgetsByType(this.$route.meta.type);
      } catch (error) {
        console.error(error);
      }

      this.loading = false;
    },
    onManagingWidgetCreation(widget) {
      this.currentWidget = widget;

      if (this.$validate.isEmptyArray(this.requiredSenderFields)) {
        this.onSubmit(widget);
        return;
      }

      if (this.requiredSenderFields.every(field => this.getCurrentProject.senderFields[field.name].selected)) {
        this.onSubmit(widget);
        return;
      }

      this.isShownSaveConfirmation = true;
    },
    async onConfirmationReceived() {
      const { senderFields } = this.getCurrentProject;

      const SINGLE_FIELDS_FOR_SELECTION = ['address', 'comment', 'message'];
      const requiredSenderKeys = this.requiredSenderFields.map(field => field.name);

      if (SINGLE_FIELDS_FOR_SELECTION.some(singleField => requiredSenderKeys.includes(singleField))) {
        SINGLE_FIELDS_FOR_SELECTION.forEach(field => {
          senderFields[field].selected = false;
        });
      }

      this.requiredSenderFields.forEach(field => {
        senderFields[field.name].selected = true;
      });

      const payload = Object.assign({}, this.getCurrentProject, { senderFields });

      try {
        await updateProject(payload);
        this.onSubmit(this.currentWidget);
      } catch {
        this.$alert({
          type: 'warning',
          text: this.$t('problems.on.server.try.later'),
        });
      }
    },
    async onSubmit(widget) {
      this.loading = true;

      const isEditing = this.$validate.isNotNull(widget.code);

      try {
        const responseData = isEditing
          ? await updateWidget(widget)
          : await createWidget(widget);

        this.$alert({
          type: 'success',
          text: isEditing ? this.$t('widget.changed.successfully') : this.$t('widget.created.successfully'),
        });

        this.$router.push({
          name: 'streamer.widgets.ready',
          params: {
            widget: {
              ...responseData,
              type: widget.type,
            },
          },
        });
      } catch {
        this.$alert({
          type: 'warning',
          text: this.$t('widget.not.saved'),
        });

        this.loading = false;
      }

      this.currentWidget = null;
    },
    async onRemove(data) {
      try {
        await removeWidget(data);
        this.REMOVE_WIDGET_BY_CODE(data.code);

        this.$alert({
          type: 'success',
          text: this.$t('widget.deleted.successfully'),
        });
      } catch {
        this.$alert({
          type: 'warning',
          text: this.$t('widget.not.deleted'),
        });
      }
    },
    async onChangeStatus(data) {
      const toggledStatus = data.status === 'ACTIVE'
        ? 'INACTIVE' : 'ACTIVE';

      try {
        await updateWidget({
          ...data,
          status: toggledStatus,
        });

        this.CHANGE_STATUS({
          code: data.code,
          status: toggledStatus,
        });

        this.$alert({
          type: 'success',
          text: this.$t('widget.changed.successfully'),
        });
      } catch {
        this.$alert({
          type: 'warning',
          text: this.$t('widget.not.saved'),
        });
      }
    },
    async handleScroll() {
      if (this.loading || this.isLastDataChunk) {
        return;
      }

      if (isOffsetExceedsOrEqualsHeight()) {
        this.INCREMENT_PAGE();
        await this.fetchWidgetsByType(this.$route.meta.type);
      }
    },
    navigateOnDesktop() {
      if (BASE_WIDGETS_ROUTE_LIST.includes(this.$route.path)) {
        this.$router.push({ name: 'cabinet' });
        return;
      }

      const routeName = this.$validate.isEmptyArray(this.getDonationGoal)
        ? 'cabinet' : 'streamer.widgets.goal';

      this.$router.push({ name: routeName });
    },
    navigateOnMobile() {
      if (BASE_WIDGETS_ROUTE_LIST.includes(this.$route.path)) {
        this.$router.push({ name: 'streamer.widgets' });
        return;
      }

      if (this.$route.path === GOAL_FORM_WIDGET_ROUTE) {
        const routeName = this.$validate.isEmptyArray(this.getAllWidgets)
          ? 'streamer.widgets' : 'streamer.widgets.goal';

        this.$router.push({ name: routeName });
        return;
      }

      this.$router.push({ name: 'cabinet' });
    },
    ...mapMutations('streamerWidgets', [
      'RESET_DATA',
      'CHANGE_STATUS',
      'REMOVE_WIDGET_BY_CODE',
      'INCREMENT_PAGE',
    ]),
    ...mapActions('streamerWidgets', ['fetchWidgetsByType']),
  },
};
</script>

<style lang="sass" module>
  .container
    +size(100%, auto)
    max-width: 500px
    margin: 0 auto
    padding: 20px 0

    +responsive('large')
      max-width: 940px
      padding: 60px 0

  .header
    +flexbox
    +flex-direction(column)
    +justify-content(center)
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

    +respond-to('medium')
      +size(620px, auto)
      +flex-direction(row)
      margin: 0 auto
      padding-bottom: 20px

  .navigation
    width: 100%
    position: relative
    margin-top: 20px

    +respond-to('medium')
      margin: 0
</style>
